/*!
 * Copyright 2026 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useContext, useMemo } from "react";

import AppContext from "~/utils/context/appContext";
import { DEFAULT_APP_PARAMS } from "~/utils/context/appParams.constants";
import {
  persistedLogsApi,
  useGetPersistedSessionLogsForModalQuery,
} from "../persistedLogs/api/persistedLogs.api";
import {
  useGetResourcePoolsByResourcePoolIdQuery,
  useGetResourcePoolsQuery,
} from "../sessionsV2/api/computeResources.api";
import {
  SessionResponse,
  sessionsV2Api,
  useGetSessionsBySessionIdLogsQuery,
  useGetSessionsBySessionIdQuery,
} from "../sessionsV2/api/sessionsV2.api";
import LogsModal from "./LogsModal";

const SESSION_LOGS_MAX_LINES = 250;

interface SessionLogsModalProps {
  isOpen: boolean;
  sessionName: string;
  toggle: () => void;
}

export default function SessionLogsModal({
  isOpen,
  sessionName,
  toggle,
}: SessionLogsModalProps) {
  const { params } = useContext(AppContext);
  const persistedLogsEnabled =
    params?.PERSISTED_LOGS_ENABLED ?? DEFAULT_APP_PARAMS.PERSISTED_LOGS_ENABLED;

  const { data: session } = useGetSessionsBySessionIdQuery(
    sessionName ? { sessionId: sessionName } : skipToken,
  );

  const { data: resourcePools } = useGetResourcePoolsQuery(
    session?.resource_class_id ? {} : skipToken,
  );
  const resourcePool = useMemo(
    () =>
      session?.resource_class_id
        ? resourcePools?.find(({ classes }) =>
            classes.some(({ id }) => id === session.resource_class_id),
          )
        : undefined,
    [resourcePools, session],
  );
  const { data: resourcePoolInfo } = useGetResourcePoolsByResourcePoolIdQuery(
    resourcePool ? { resourcePoolId: resourcePool.id } : skipToken,
  );

  // NOTE: persisted logs cannot work if `cluster` is defined on the resource pool (remote k8s cluster)
  if (
    persistedLogsEnabled &&
    session?.session_type === "non-interactive" &&
    resourcePoolInfo != null &&
    resourcePoolInfo.cluster == null
  ) {
    return (
      <PersistedLogsModal
        isOpen={isOpen}
        session={session}
        sessionName={sessionName}
        toggle={toggle}
      />
    );
  }

  return (
    <KubernetesLogsModal
      isOpen={isOpen}
      session={session}
      sessionName={sessionName}
      toggle={toggle}
    />
  );
}

interface KubernetesLogsModalProps extends SessionLogsModalProps {
  session: SessionResponse | undefined;
}

function KubernetesLogsModal({
  isOpen,
  session,
  sessionName,
  toggle,
}: KubernetesLogsModalProps) {
  const query = useGetSessionsBySessionIdLogsQuery(
    isOpen
      ? {
          sessionId: sessionName,
          maxLines: SESSION_LOGS_MAX_LINES,
        }
      : skipToken,
  );

  const [trigger] =
    sessionsV2Api.endpoints.getSessionsBySessionIdLogs.useLazyQuery();
  const downloadQueryTrigger = useCallback(
    () => trigger({ sessionId: sessionName }),
    [sessionName, trigger],
  );

  return (
    <LogsModal
      isOpen={isOpen}
      name={sessionName}
      query={query}
      downloadQueryTrigger={downloadQueryTrigger}
      title={"Logs"}
      toggle={toggle}
      sessionState={session?.status?.state}
      sessionError={
        session?.status?.state === "failed"
          ? session?.status?.message
          : undefined
      }
      defaultTab="amalthea-session"
    />
  );
}

interface PersistedLogsModalProps extends SessionLogsModalProps {
  session: SessionResponse;
}

function PersistedLogsModal({
  isOpen,
  session,
  sessionName,
  toggle,
}: PersistedLogsModalProps) {
  const query = useGetPersistedSessionLogsForModalQuery(
    isOpen
      ? {
          launcherId: session.launcher_id,
          params: {
            submission_id: session.submission_id,
          },
        }
      : skipToken,
  );

  const [trigger] =
    persistedLogsApi.endpoints.getPersistedSessionLogsForModal.useLazyQuery();
  const downloadQueryTrigger = useCallback(
    () =>
      trigger({
        launcherId: session.launcher_id,
        params: { submission_id: session.submission_id },
      }),
    [session.launcher_id, session.submission_id, trigger],
  );

  return (
    <LogsModal
      isOpen={isOpen}
      name={sessionName}
      query={query}
      downloadQueryTrigger={downloadQueryTrigger}
      title={"Logs"}
      toggle={toggle}
      sessionState={session?.status?.state}
      sessionError={
        session?.status?.state === "failed"
          ? session?.status?.message
          : undefined
      }
      defaultTab="amalthea-session"
    />
  );
}
